import React, { useState, useRef, useEffect } from 'react'
import { ChevronDown, CheckCircle, Loader2, X } from 'lucide-react'
import { Input } from './Input'
import { cn } from '@/lib/utils'

export interface AutocompleteOption {
  id: string
  label: string
  image?: string
  meta?: string
}

interface AutocompleteInputProps {
  value: string
  options: AutocompleteOption[]
  selectedId?: string
  onChange: (value: string) => void
  onSelect: (option: AutocompleteOption) => void
  placeholder?: string
  disabled?: boolean
  loading?: boolean
  error?: string
  leftIcon?: React.ReactNode
  emptyMessage?: string
}

export const AutocompleteInput: React.FC<AutocompleteInputProps> = ({
  value,
  options,
  selectedId,
  onChange,
  onSelect,
  placeholder,
  disabled = false,
  loading = false,
  error,
  leftIcon,
  emptyMessage = 'Nenhum resultado encontrado'
}) => {
  const [showDropdown, setShowDropdown] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const filteredOptions = options.filter(option =>
    option.label.toLowerCase().includes(value.toLowerCase())
  )

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.value)
    setShowDropdown(true)
  }

  const handleOptionClick = (option: AutocompleteOption) => {
    onSelect(option)
    onChange(option.label)
    setShowDropdown(false)
  }

  const handleClear = () => {
    onChange('')
    setShowDropdown(false)
  }

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  return (
    <div className="relative" ref={dropdownRef}>
      <div className="relative">
        <Input
          type="text"
          value={value}
          onChange={handleInputChange}
          onFocus={() => setShowDropdown(true)}
          placeholder={placeholder}
          disabled={disabled || loading}
          className={cn(
            "pr-16",
            error && "border-error focus-visible:ring-error focus-visible:border-error"
          )}
        />
        {leftIcon && (
          <div className="absolute left-3 top-1/2 -translate-y-1/2">
            {leftIcon}
          </div>
        )}
        {loading && (
          <div className="absolute right-12 top-1/2 -translate-y-1/2">
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          </div>
        )}
        <button
          type="button"
          onClick={() => setShowDropdown(!showDropdown)}
          disabled={disabled}
          className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-surface rounded transition-colors disabled:opacity-50"
          aria-label={showDropdown ? "Fechar lista" : "Mostrar lista"}
        >
          <ChevronDown className={cn("h-4 w-4 transition-transform", showDropdown && "rotate-180")} />
        </button>
        {value && !loading && !disabled && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-9 top-1/2 -translate-y-1/2 p-1 hover:bg-surface rounded transition-colors"
            aria-label="Limpar"
          >
            <X className="h-3.5 w-3.5 text-muted-foreground hover:text-muted-foreground" />
          </button>
        )}
      </div>
      {error && (
        <p className="mt-1 text-sm text-error">{error}</p>
      )}
      {showDropdown && !disabled && (
        <div className="absolute z-50 w-full mt-1 bg-card border border-border rounded-lg shadow-lg max-h-60 overflow-y-auto">
          {loading ? (
            <div className="p-4 text-center text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin mx-auto mb-2" />
              Carregando...
            </div>
          ) : filteredOptions.length === 0 ? (
            <div className="p-4 text-center text-muted-foreground text-sm">
              {emptyMessage}
            </div>
          ) : (
            <div className="py-1">
              {filteredOptions.map((option) => (
                <button
                  key={option.id}
                  type="button"
                  onClick={() => handleOptionClick(option)}
                  className="w-full px-4 py-2 flex items-center justify-between hover:bg-muted text-left"
                >
                  <div className="flex items-center space-x-2">
                    {option.image && (
                      <img
                        src={option.image}
                        alt=""
                        className="w-6 h-6 rounded-full object-cover"
                      />
                    )}
                    <div>
                      <div className="font-medium text-sm">{option.label}</div>
                      {option.meta && (
                        <div className="text-xs text-muted-foreground">{option.meta}</div>
                      )}
                    </div>
                  </div>
                  {option.id === selectedId && (
                    <CheckCircle className="h-4 w-4 text-accent" />
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}